/**
 * OrderToolsWidget component.
 * @module components/Widgets/OrderToolsWidget
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Input,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';

import { injectIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';
import { Icon } from '@plone/volto/components';
import { getContent, getVocabulary } from '@plone/volto/actions';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import {
  customSelectStyles,
  DropdownIndicator,
  ClearIndicator,
  selectTheme,
} from '@plone/volto/components/manage/Widgets/SelectStyling';

import ToolPickerModal from './ToolPickerModal';

import addSVG from '@plone/volto/icons/add.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import circlePlusSVG from '@plone/volto/icons/add.svg';
import circleMinusSVG from '@plone/volto/icons/remove.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

const parseRecords = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

// The shipping_fee field is stored as {"enabled": boolean, "amount": number}.
const parseShippingFee = (value) => {
  try {
    const parsed = JSON.parse(value);
    return {
      enabled: !!parsed?.enabled,
      amount: Number(parsed?.amount) || 0,
    };
  } catch (error) {
    return { enabled: false, amount: 0 };
  }
};

const getTotal = (record) =>
  record.quantity === undefined || record.price === undefined
    ? 0
    : record.quantity * record.price;

const getSubtotal = (records) =>
  records.reduce((sum, record) => sum + getTotal(record), 0);

const getCreditCardFee = (base) => (Number(base) || 0) * 0.04;

// The $5 EUR markup is already baked into each record's own price as soon
// as a size/set is selected (see the size Select's onChange below), so
// totals built from those prices are already correct in either currency -
// this only needs to swap the display symbol, not add anything further.
const formatAmount = (amount, currency) => {
  const value = Number(amount) || 0;
  const symbol = currency === 'EUR' ? '€' : '$';
  return `${symbol} ${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatTotal = (record, currency) =>
  formatAmount(getTotal(record), currency);

/**
 * OrderToolRow, a single row in the OrderToolsWidget table.
 * Split out from OrderToolsWidget so it can fetch the selected tool's
 * sizes/sets via its own hooks, independent of the other rows.
 */
const OrderToolRow = ({
  fieldId,
  index,
  record,
  isDisabled,
  Select,
  toolOptions,
  currency,
  onRecordChange,
  onPriceChange,
  onQuantityChange,
  onRemove,
}) => {
  const dispatch = useDispatch();
  const subrequest = `order-tool-${index}`;
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (record.tool) {
      dispatch(getContent(`/tools/${record.tool}`, null, subrequest));
    }
  }, [dispatch, record.tool, subrequest]);

  const onSelectTool = (toolId) => {
    onRecordChange(index, { tool: toolId, size: '', price: 0 });
    setPickerOpen(false);
  };

  const toolContent = useSelector(
    (state) => state.content.subrequests?.[subrequest]?.data,
  );

  const sizeOptions = (toolContent?.items || [])
    .filter((item) => item.sold_separate)
    .sort((a, b) => {
      if (a['@type'] !== b['@type']) {
        return a['@type'] === 'Set' ? -1 : 1;
      }
      return (a.title || a.id || '').localeCompare(
        b.title || b.id || '',
        undefined,
        { numeric: true },
      );
    })
    .map((item) => ({
      label: `${item['@type']} ${item.title || item.id}`,
      value: item.id,
    }));

  return (
    <TableRow>
      <TableCell>
        <div className="tool-widget-wrapper">
          <div
            id={`field-${fieldId}-tool-${index}`}
            className={`tool-widget-value${record.tool ? '' : ' placeholder'}`}
            role="button"
            tabIndex={0}
            onClick={() => !isDisabled && setPickerOpen(true)}
            onKeyDown={(e) => {
              if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                setPickerOpen(true);
              }
            }}
          >
            {toolOptions.find((option) => option.value === record.tool)
              ?.label || 'Select tool'}
          </div>
          {record.tool ? (
            <button
              type="button"
              className="item ui noborder button"
              disabled={isDisabled}
              onClick={() =>
                onRecordChange(index, { tool: '', size: '', price: 0 })
              }
              aria-label="Clear"
            >
              <Icon name={clearSVG} size="20px" className="close" />
            </button>
          ) : (
            <button
              type="button"
              className="item ui noborder button"
              disabled={isDisabled}
              onClick={() => setPickerOpen(true)}
              aria-label="Select a tool"
            >
              <Icon name={addSVG} size="20px" />
            </button>
          )}
        </div>
        <ToolPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={onSelectTool}
          selectedId={record.tool}
        />
      </TableCell>
      <TableCell>
        <Select
          id={`field-${fieldId}-size-${index}`}
          name={`${fieldId}-size-${index}`}
          isDisabled={isDisabled || !record.tool}
          isSearchable={false}
          isClearable
          menuPlacement="auto"
          maxMenuHeight={1600}
          className="react-select-container"
          classNamePrefix="react-select"
          options={sizeOptions}
          styles={customSelectStyles}
          theme={selectTheme}
          components={{ DropdownIndicator, ClearIndicator }}
          value={
            sizeOptions.find((option) => option.value === record.size) || null
          }
          placeholder="Select size/set"
          onChange={(selected) => {
            const item = (toolContent?.items || []).find(
              (option) => option.id === selected?.value,
            );
            // The item's own price is always USD; in EUR the editable
            // price starts at that USD price plus the flat $5 markup.
            const basePrice = Number(item?.price) || 0;
            const price = currency === 'EUR' ? basePrice + 5 : basePrice;
            onRecordChange(index, {
              size: selected ? selected.value : undefined,
              ...(selected ? { price } : {}),
            });
          }}
        />
      </TableCell>
      <TableCell>
        <Input
          fluid
          disabled={isDisabled}
          inputmode="numeric"
          pattern="[0-9]*"
          value={record.no}
          onChange={(e) => onRecordChange(index, { no: e.target.value })}
        />
      </TableCell>
      <TableCell>
        <Input
          fluid
          disabled={isDisabled}
          inputmode="numeric"
          pattern="[0-9]*"
          value={record.price}
          onChange={(e) => onPriceChange(index, e.target.value)}
        />
      </TableCell>
      <TableCell>
        <div className="quantity-input">
          <button
            type="button"
            className="circular-button"
            disabled={isDisabled}
            onClick={() =>
              onRecordChange(index, {
                quantity: Math.max(0, (Number(record.quantity) || 0) - 1),
              })
            }
          >
            <Icon name={circleMinusSVG} size="24px" />
          </button>
          <Input
            fluid
            type="number"
            step="1"
            min="0"
            disabled={isDisabled}
            value={record.quantity}
            onChange={(e) => onQuantityChange(index, e.target.value)}
          />
          <button
            type="button"
            className="circular-button"
            disabled={isDisabled}
            onClick={() =>
              onRecordChange(index, {
                quantity: (Number(record.quantity) || 0) + 1,
              })
            }
          >
            <Icon name={circlePlusSVG} size="24px" />
          </button>
        </div>
      </TableCell>
      <TableCell className="total-cell">
        <span>{formatTotal(record, currency)}</span>
        <button
          type="button"
          className="circular-button"
          disabled={isDisabled}
          onClick={() => onRemove(index)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
      </TableCell>
    </TableRow>
  );
};

/**
 * OrderToolsWidget
 */
const OrderToolsWidget = (props) => {
  const { id, value, onChange, isDisabled, reactSelect } = props;
  const Select = reactSelect.default;
  const records = parseRecords(value);
  const dispatch = useDispatch();

  // React keys for the rows below, kept entirely out of the stored JSON.
  // Rows are only ever appended or removed (never reordered), so growing
  // this lazily and splicing it in lockstep in onRemove keeps each key
  // correctly attached to its own row across renders.
  const keysRef = useRef([]);
  while (keysRef.current.length < records.length) {
    keysRef.current.push(uuid());
  }

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: 'tools' }));
  }, [dispatch]);

  // If no tools are set yet, start with one empty row.
  useEffect(() => {
    if (records.length === 0) {
      onChange(
        id,
        JSON.stringify([{ tool: '', size: '', no: '', quantity: 1, price: 0 }]),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toolOptions = useSelector(
    (state) => state.vocabularies?.tools?.items || [],
  );

  // The order's own currency field, not the sidebar's global toggle.
  const currency = useSelector((state) => state.form?.global?.currency);

  const creditCardFee = useSelector(
    (state) => state.form?.global?.credit_card_fee,
  );

  const shippingFeeValue = useSelector(
    (state) => state.form?.global?.shipping_fee,
  );
  const shippingFee = parseShippingFee(shippingFeeValue);

  const subtotalAmount = getSubtotal(records);
  const shippingAmount = shippingFee.enabled ? shippingFee.amount : 0;
  // Credit card fee is charged on the subtotal AND the shipping fee.
  const creditCardFeeAmount = creditCardFee
    ? getCreditCardFee(subtotalAmount + shippingAmount)
    : 0;
  const grandTotal = subtotalAmount + shippingAmount + creditCardFeeAmount;

  const onAdd = () => {
    onChange(
      id,
      JSON.stringify([
        ...records,
        { tool: '', size: '', no: '', quantity: 1, price: 0 },
      ]),
    );
  };

  const onRecordChange = (index, updates) => {
    const updated = records.map((record, i) =>
      i === index ? { ...record, ...updates } : record,
    );
    onChange(id, JSON.stringify(updated));
  };

  const onPriceChange = (index, inputValue) => {
    const cleaned = inputValue.replace(/[^0-9.]/g, '');
    onRecordChange(index, { price: cleaned === '' ? 0 : Number(cleaned) });
  };

  const onQuantityChange = (index, inputValue) => {
    const cleaned = inputValue.replace(/[^0-9]/g, '');
    onRecordChange(index, { quantity: cleaned === '' ? 0 : Number(cleaned) });
  };

  const onRemove = (index) => {
    keysRef.current.splice(index, 1);
    onChange(id, JSON.stringify(records.filter((_, i) => i !== index)));
  };

  return (
    <FormFieldWrapper {...props} className="order-tools" columns={1}>
      <Table celled id={`field-${id}`} disabled={isDisabled}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell
              colspan="6"
              textAlign="center"
              className="main buttons"
            >
              <span className="th-title">Tools</span>
              <button
                type="button"
                className="circular-button primary float-left"
                disabled={isDisabled}
                onClick={onAdd}
              >
                <Icon name={addSVG} size="24px" />
              </button>
            </TableHeaderCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell className="three-half wide">Tool</TableHeaderCell>
            <TableHeaderCell className="two-half wide">
              Size / Set
            </TableHeaderCell>
            <TableHeaderCell width="1">No.</TableHeaderCell>
            <TableHeaderCell width="1">Price</TableHeaderCell>
            <TableHeaderCell width="2">Quantity</TableHeaderCell>
            <TableHeaderCell width="2">Total</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record, index) => (
            <OrderToolRow
              key={keysRef.current[index]}
              fieldId={id}
              index={index}
              record={record}
              isDisabled={isDisabled}
              Select={Select}
              toolOptions={toolOptions}
              currency={currency}
              onRecordChange={onRecordChange}
              onPriceChange={onPriceChange}
              onQuantityChange={onQuantityChange}
              onRemove={onRemove}
            />
          ))}
          {(creditCardFee || shippingFee.enabled) && (
            <TableRow>
              <TableCell colspan="5">Subtotal</TableCell>
              <TableCell>{formatAmount(subtotalAmount, currency)}</TableCell>
            </TableRow>
          )}
          {shippingFee.enabled && (
            <TableRow>
              <TableCell colspan="5">Shipping fee</TableCell>
              <TableCell>{formatAmount(shippingAmount, currency)}</TableCell>
            </TableRow>
          )}
          {creditCardFee && (
            <TableRow>
              <TableCell colspan="5">Credit card fee (4%)</TableCell>
              <TableCell>
                {formatAmount(creditCardFeeAmount, currency)}
              </TableCell>
            </TableRow>
          )}
          <TableRow className="grand-total-row">
            <TableCell colspan="5">
              <button
                type="button"
                className="circular-button primary float-left"
                disabled={isDisabled}
                onClick={onAdd}
              >
                <Icon name={addSVG} size="24px" />
              </button>
            </TableCell>
            <TableCell>
              <strong>{formatAmount(grandTotal, currency)}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </FormFieldWrapper>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
OrderToolsWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  wrapped: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

/**
 * Default properties.
 * @property {Object} defaultProps Default properties.
 * @static
 */
OrderToolsWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  onChange: null,
  onEdit: null,
  onDelete: null,
};

export default injectLazyLibs(['reactSelect'])(injectIntl(OrderToolsWidget));
